import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnChanges,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NgControl,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { UserRole } from '@qaroni-app/workers/types/user-role';
import { QaroniBaseFormsDirective } from '@qaroni-core/directives/qaroni-base-forms/qaroni-base-forms.directive';

@Component({
  selector: 'qaroni-select-user-form-field',
  templateUrl: './select-user-form-field.component.html',
  styleUrl: './select-user-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectUserFormFieldComponent
  extends QaroniBaseFormsDirective
  implements OnInit, OnChanges, ControlValueAccessor
{
  readonly users = input<UserRole[]>([]);
  readonly excludedIds = input<number[]>([]);
  readonly label = input<string>($localize`Usuario`);
  readonly subscriptSizing = input<'dynamic' | 'fixed'>('fixed');

  private readonly cdr = inject(ChangeDetectorRef);
  public readonly ngControl = inject(NgControl, { optional: true, self: true });

  public readonly searchControl = new FormControl<string | UserRole>('');
  public filteredUsers: UserRole[] = [];

  public readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => this.hasError,
  };

  private selectedUserId: number | null = null;
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  private readonly searchValueChanges$ = this.searchControl.valueChanges;

  constructor() {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.subs.add(this.searchValueChanges$.subscribe(this.getSearchChanges));
  }

  ngOnChanges(): void {
    this.syncSelectedUser();
    this.filterUsers(this.searchText);
  }

  get control(): AbstractControl | null {
    return this.ngControl?.control ?? null;
  }

  get hasError(): boolean {
    return (
      !!this.control && this.InputValidation.checkInputErrors(this.control)
    );
  }

  get filterNoResults(): boolean {
    return this.filteredUsers.length === 0;
  }

  private get searchText(): string {
    const value = this.searchControl.value;
    return typeof value === 'string' ? value : '';
  }

  public displayUser = (user: UserRole | string | null): string => {
    if (!user) {
      return '';
    }

    return typeof user === 'string' ? user : (user.user?.fullName ?? '');
  };

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const user: UserRole = event.option.value;
    this.updateValue(user.userId);
  }

  public onBlur(): void {
    this.onTouched();
  }

  public onPanelClosed(): void {
    if (typeof this.searchControl.value === 'string') {
      this.searchControl.setValue(this.selectedUser ?? '', {
        emitEvent: false,
      });
    }

    this.filterUsers('');
    this.cdr.markForCheck();
  }

  public writeValue(value: number | null): void {
    this.selectedUserId = value ?? null;
    this.syncSelectedUser();
    this.filterUsers('');
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.searchControl.enable({ emitEvent: false });
    }
  }

  private get selectedUser(): UserRole | null {
    return (
      this.users().find(user => user.userId === this.selectedUserId) ?? null
    );
  }

  private syncSelectedUser(): void {
    if (typeof this.searchControl.value === 'string' && this.searchText) {
      return;
    }

    this.searchControl.setValue(this.selectedUser ?? '', { emitEvent: false });
  }

  private filterUsers(search: string): void {
    const searchTerm = search.trim().toLowerCase();
    const excludedIds = this.excludedIds().filter(
      id => id !== this.selectedUserId
    );

    this.filteredUsers = this.users().filter(
      user =>
        !excludedIds.includes(user.userId) &&
        (!searchTerm ||
          (user.user?.fullName ?? '').toLowerCase().includes(searchTerm))
    );
  }

  private updateValue(userId: number | null): void {
    if (this.selectedUserId === userId) {
      return;
    }

    this.selectedUserId = userId;
    this.onChange(userId);
  }

  private getSearchChanges = (value: string | UserRole): void => {
    if (typeof value !== 'string') {
      return;
    }

    this.updateValue(null);
    this.filterUsers(value);
  };
}
